import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  isLocaleSupported,
  type SupportedLocale,
} from './locale-constants';

/**
 * Maps a raw locale tag to a SupportedLocale.
 * Exact (case-insensitive) first, then base-language (fr → fr-CA, en-US → en).
 * Used to normalize an app locale before handing it to a third party; browser
 * sniffing deliberately stays exact-match (see getBrowserLocale).
 */
export const matchSupportedLocale = (
  value: string | undefined | null,
): SupportedLocale | undefined => {
  if (!value) {
    return undefined;
  }
  const lower = value.toLowerCase();
  const exact = SUPPORTED_LOCALES.find(
    (locale) => locale.toLowerCase() === lower,
  );
  if (exact) {
    return exact;
  }
  const [baseLang] = lower.split('-');
  return SUPPORTED_LOCALES.find(
    (locale) =>
      locale.toLowerCase() === baseLang ||
      locale.toLowerCase().startsWith(`${baseLang}-`),
  );
};

/**
 * Resolves the browser's preferred locale to a SupportedLocale.
 * Exact-match only against SUPPORTED_LOCALES; unsupported values fall
 * back to DEFAULT_LOCALE. Used pre-login when user/org preferences are
 * unknown.
 */
export const getBrowserLocale = (): SupportedLocale => {
  if (typeof navigator === 'undefined') {
    return DEFAULT_LOCALE;
  }
  const language = navigator.language;
  if (!language) {
    return DEFAULT_LOCALE;
  }
  return isLocaleSupported(language) ? language : DEFAULT_LOCALE;
};
